import React from 'react'
import { twMerge } from 'tailwind-merge'

import { Button } from '../button'
import { HStack } from '../hstack'
import { Pressable } from '../pressable'
import { Text } from '../text'

/** Semantic counter state, decoupled from `aos-helpers`' LVP status helper. */
export type LVPCounterViewStatus = 'success' | 'warning' | 'critical'

export interface LVPCounterViewProps {
    title: string
    status: LVPCounterViewStatus
    counterValue?: string
    /** Reveals the action buttons (hover on web). */
    isExpanded: boolean
    canEditTasks: boolean
    inspectionLabel: string
    offLabel: string
    onInspectionPress: () => void
    onOffPress: () => void
    onHoverIn?: () => void
    onHoverOut?: () => void
    className?: string
    testID?: string
}

const BORDER_CLASS: Record<LVPCounterViewStatus, string> = {
    success: 'border-status-success',
    warning: 'border-status-warning',
    critical: 'border-status-critical',
}

/**
 * LVP counter card (AOS-1135 redesign): status-coloured border, uppercase
 * muted title, large timer readout and the Inspection / Off actions revealed
 * on hover. Presentational — the host owns the countdown and the expansion
 * state.
 */
export const LVPCounterView: React.FC<LVPCounterViewProps> = ({
    title,
    status,
    counterValue,
    isExpanded,
    canEditTasks,
    inspectionLabel,
    offLabel,
    onInspectionPress,
    onOffPress,
    onHoverIn,
    onHoverOut,
    className,
    testID,
}) => (
    <Pressable
        onHoverIn={onHoverIn}
        onHoverOut={onHoverOut}
        testID={testID}
        className={twMerge(
            'min-w-[250px] cursor-default items-center gap-2 overflow-hidden',
            'rounded border-2 border-solid bg-background-elevation px-6 py-4',
            'transition-[height] duration-500 ease-in-out',
            isExpanded ? 'h-[145px]' : 'h-[95px]',
            BORDER_CLASS[status],
            className,
        )}
    >
        <Text size='body' tone='muted' className='uppercase'>
            {title}
        </Text>

        <Text size='h1' tone={status === 'critical' ? 'critical' : 'default'} className='uppercase'>
            {counterValue}
        </Text>

        {canEditTasks && (
            <HStack
                className={twMerge(
                    'w-full gap-2 transition-opacity duration-500',
                    isExpanded ? 'opacity-100' : 'opacity-0',
                )}
            >
                <Button
                    onPress={onInspectionPress}
                    type='primary'
                    className='h-8 flex-1'
                    testID={testID ? `${testID}-inspection-button` : undefined}
                >
                    {inspectionLabel}
                </Button>
                <Button
                    onPress={onOffPress}
                    type='secondary'
                    className='h-8 flex-1'
                    testID={testID ? `${testID}-off-button` : undefined}
                >
                    {offLabel}
                </Button>
            </HStack>
        )}
    </Pressable>
)

LVPCounterView.displayName = 'LVPCounterView'
